import {
  DiceRollActionTypes,
  DieEventTypes,
  DISPOSE_LENGTH,
  initialEntityState,
  PddPayloadTypes,
  SETTLED_LENGTH,
} from "@/constants";
import { DieEventValues } from "@/enums";
import type {
  CurrentEvent,
  DiceSet,
  Die,
  Entities,
  Manifest,
  Roll,
  UserSettings,
} from "@/types";
import type { Message } from "@dndbeyond/message-broker-lib";
import { invalidate } from "@react-three/fiber";
import { current, produce } from "immer";
import mitt from "mitt";
import { v4 as uuid } from "uuid";
import { create } from "zustand";

const eventEmitter = mitt<{ event: { id: string; trigger?: string } }>();

type RenderedRollMessage = {
  roll: Roll;
  dice: Record<string, Die>;
};
type RenderedDiceState = {
  manifests: Entities<Manifest>;
  resetRenderedDiceStore: () => void;
  addManifest: (id: string, manifest: Manifest) => void;
  setManifests: (manifests: Record<string, Manifest>) => void;
  renderedDice: Entities<Die>;
  rolls: Entities<Roll>;
  addRenderedRoll: (payload: RenderedRollMessage) => void;
  clearRenderedRoll: (id: string) => void;
  setCurrentEvent: (
    id: string,
    event?: CurrentEvent,
    isPreview?: boolean,
    userId?: string,
    postMessage?: (message: {
      type: string;
      payload: Message | string;
    }) => void,
  ) => void;
  setRollFinished: (
    rollId: string,
    userId?: string,
    postMessage?: (message: {
      type: string;
      payload: Message | string;
    }) => void,
  ) => void;
  setDiceSets: (diceSets: DiceSet[]) => void;
  diceSets?: DiceSet[];
  onDiceEvent: typeof eventEmitter.on;
  offDiceEvent: typeof eventEmitter.off;
  userSettings?: UserSettings;
  setUserSettings: (settings: UserSettings) => void;
};

const getDieEventValue = (trigger?: string | null) => {
  if (!trigger) return undefined;

  return DieEventValues[trigger as keyof typeof DieEventValues];
};

export const useRenderedDiceStore = create<RenderedDiceState>()((set) => ({
  renderedDice: initialEntityState,
  rolls: initialEntityState,
  manifests: initialEntityState,
  resetRenderedDiceStore: () => {
    set({
      renderedDice: initialEntityState,
      rolls: initialEntityState,
    });
  },
  addRenderedRoll: (payload: RenderedRollMessage) => {
    set(
      produce(({ rolls, renderedDice }: RenderedDiceState) => {
        rolls.entities[payload.roll.id] = payload.roll;
        renderedDice.entities = {
          ...renderedDice.entities,
          ...payload.dice,
        };

        if (!rolls.ids.includes(payload.roll.id)) {
          rolls.ids.push(payload.roll.id);
          renderedDice.ids.push(...Object.keys(payload.dice));
        }
      }),
    );
    invalidate();
  },
  clearRenderedRoll: (id: string) => {
    set(
      produce(({ rolls, renderedDice }: RenderedDiceState) => {
        const rollIdx = rolls.ids.indexOf(id);

        if (rollIdx === -1) return;

        rolls.ids.splice(rollIdx, 1);
        delete rolls.entities[id];

        Object.values(renderedDice.entities)
          .filter(({ rollId }) => rollId === id)
          .forEach(({ id: diceId }) => {
            const diceIdx = renderedDice.ids.indexOf(diceId);

            if (diceIdx === -1) return;

            renderedDice.ids.splice(diceIdx, 1);
            delete renderedDice.entities[diceId];
          });
      }),
    );
  },
  addManifest: (id: string, manifest: Manifest) => {
    set(
      produce((state: RenderedDiceState) => {
        if (state.manifests.ids.includes(id)) return;

        state.manifests.entities[id] = manifest;
        state.manifests.ids.push(id);
      }),
    );
  },
  setManifests: (manifests: Record<string, Manifest>) => {
    set(
      produce((state: RenderedDiceState) => {
        Object.keys(manifests).forEach((setId) => {
          if (state.manifests.ids.includes(setId)) return;

          state.manifests.entities[setId] = manifests[setId];

          state.manifests.ids.push(setId);
        });
      }),
    );
  },
  setCurrentEvent: (id, event, isPreview, userId, postMessage) => {
    let rollIdToFinish: string | undefined;

    set(
      produce(({ renderedDice: { entities } }: RenderedDiceState) => {
        if (!entities[id]) return;

        eventEmitter.emit("event", { id, trigger: event?.trigger });
        entities[id].lastStandardEvent = getDieEventValue(event?.trigger);

        if (event?.trigger === DieEventTypes.ROLL_COMPLETE) {
          if (isPreview) return;

          const { rollId } = entities[id];
          const rollDiceEntities = Object.values(entities).filter(
            ({ rollId: rId }) => rId === rollId,
          );

          if (
            !rollDiceEntities.every(
              ({ lastStandardEvent }) =>
                lastStandardEvent === DieEventValues.rollComplete ||
                lastStandardEvent === DieEventValues.minRoll ||
                lastStandardEvent === DieEventValues.maxRoll,
            )
          )
            return;

          rollIdToFinish = rollId;
        }
      }),
    );

    if (rollIdToFinish) {
      useRenderedDiceStore
        .getState()
        .setRollFinished(rollIdToFinish, userId, postMessage);
    }
  },
  setRollFinished: (rollId, userId, postMessage) => {
    set(
      produce(({ rolls: { entities } }: RenderedDiceState) => {
        if (entities[rollId].isFinished) return;

        entities[rollId].isFinished = true;

        const rollMessage = current(entities[rollId].message);

        if (String(userId) === rollMessage.userId) {
          postMessage?.({
            type: DiceRollActionTypes.Fulfilled,
            payload: {
              ...rollMessage,
              dateTime: String(Date.now()),
              eventType: DiceRollActionTypes.Fulfilled,
              id: uuid(),
              persist: true,
            },
          });
        }

        setTimeout(() => {
          const {
            setCurrentEvent: setCurrentEventFromState,
            clearRenderedRoll: clearRenderedRollFromState,
            renderedDice: { entities: renderedDiceEntities },
          } = useRenderedDiceStore.getState();
          const rollDieIds = Object.values(renderedDiceEntities)
            .filter(({ rollId: rId }) => rId === rollId)
            .map(({ id: dieId }) => dieId);

          rollDieIds.forEach((dieId) => {
            setCurrentEventFromState(dieId, {
              trigger: DieEventTypes.DIE_REMOVED,
            });
          });
          setTimeout(() => {
            clearRenderedRollFromState(rollId);
            postMessage?.({
              type: PddPayloadTypes.RemoveRoll,
              payload: rollId,
            });
            invalidate();
          }, 1_000 * DISPOSE_LENGTH);
        }, 1_000 * SETTLED_LENGTH);
      }),
    );
  },
  setDiceSets: (diceSets) => {
    set(
      produce((state: RenderedDiceState) => {
        state.diceSets = diceSets;
      }),
    );
  },
  onDiceEvent: eventEmitter.on,
  offDiceEvent: eventEmitter.off,
  setUserSettings: (settings) => {
    set(
      produce((state: RenderedDiceState) => {
        state.userSettings = settings;
      }),
    );
  },
}));
